"use client";

import { useState, useCallback } from "react";
import { usePathname } from "@/i18n/navigation";
import { useRouter } from "@/i18n/navigation";
import Link from "next/link";
import { Maximize2, Minimize2 } from "lucide-react";
import styles from "./fullScreenCalculator.module.css";
import { useSearchParams } from "next/navigation";

function formatDisplay(value: string): string {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "0";
  if (!Number.isFinite(num)) return "Error";
  const s = String(value);
  if (s.length > 14) return num.toExponential(6);
  return s;
}

export default function FullScreenCalculator() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isFullScreen = searchParams.get("fullscreen") === "1";

  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [memory, setMemory] = useState(0);
  const [isNewInput, setIsNewInput] = useState(true);

  const getDisplayNum = useCallback(() => {
    const n = parseFloat(display);
    return Number.isNaN(n) ? 0 : n;
  }, [display]);

  const computeResult = useCallback(() => {
    const current = getDisplayNum();
    if (previousValue === null || operation === null) return current;
    switch (operation) {
      case "+":
        return previousValue + current;
      case "−":
        return previousValue - current;
      case "×":
        return previousValue * current;
      case "÷":
        return current === 0 ? NaN : previousValue / current;
      default:
        return current;
    }
  }, [previousValue, operation, getDisplayNum]);

  const handleNumber = (digit: string) => {
    if (isNewInput) {
      setDisplay(digit === "." ? "0." : digit);
      setIsNewInput(false);
    } else {
      if (digit === "." && display.includes(".")) return;
      if (digit === "." && display === "0") {
        setDisplay("0.");
        return;
      }
      if (display === "0" && digit !== ".") {
        setDisplay(digit);
        return;
      }
      if (display.length >= 14) return;
      setDisplay(display + digit);
    }
  };

  const handleOperator = (op: string) => {
    if (previousValue !== null && operation !== null) {
      const result = computeResult();
      if (Number.isNaN(result)) {
        setDisplay("Error");
        setIsNewInput(true);
        setPreviousValue(null);
        setOperation(null);
        return;
      }
      const resultStr = String(result);
      setDisplay(resultStr.length > 14 ? result.toExponential(6) : resultStr);
      setPreviousValue(result);
      setOperation(op);
    } else {
      setPreviousValue(getDisplayNum());
      setOperation(op);
    }
    setIsNewInput(true);
  };

  const handleEquals = () => {
    const result = computeResult();
    if (Number.isNaN(result)) {
      setDisplay("Error");
    } else {
      const resultStr = String(result);
      setDisplay(resultStr.length > 14 ? result.toExponential(6) : resultStr);
    }
    setPreviousValue(null);
    setOperation(null);
    setIsNewInput(true);
  };

  const handleClearEntry = () => {
    setDisplay("0");
    setIsNewInput(true);
  };

  const handleClear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setIsNewInput(true);
  };

  const handleSign = () => {
    const n = getDisplayNum();
    setDisplay(String(-n));
  };

  const handleSqrt = () => {
    const n = getDisplayNum();
    if (n < 0) {
      setDisplay("Error");
      setIsNewInput(true);
      return;
    }
    setDisplay(String(Math.sqrt(n)));
    setIsNewInput(true);
  };

  const handlePercent = () => {
    const n = getDisplayNum();
    if (previousValue !== null && operation !== null) {
      const pct = (previousValue * n) / 100;
      const result =
        operation === "+"
          ? previousValue + pct
          : operation === "−"
            ? previousValue - pct
            : operation === "×"
              ? previousValue * (n / 100)
              : previousValue / (n / 100 || 1);
      setDisplay(String(result));
      setPreviousValue(null);
      setOperation(null);
    } else {
      setDisplay(String(n / 100));
    }
    setIsNewInput(true);
  };

  const handleReciprocal = () => {
    const n = getDisplayNum();
    if (n === 0) {
      setDisplay("Error");
      setIsNewInput(true);
      return;
    }
    setDisplay(String(1 / n));
    setIsNewInput(true);
  };

  const handleMemoryClear = () => setMemory(0);
  const handleMemoryRecall = () => {
    setDisplay(String(memory));
    setIsNewInput(true);
  };
  const handleMemoryAdd = () => setMemory(memory + getDisplayNum());
  const handleMemorySub = () => setMemory(memory - getDisplayNum());

  const toggleFullScreen = () => {
    if (isFullScreen) {
      router.replace(`${pathname}`);
    } else {
      router.replace(`${pathname}?fullscreen=1`);
    }
  };

  const displayValue = formatDisplay(display);

  return (
    <div className={`${styles.wrapper} ${isFullScreen ? styles.fullScreen : ""}`}>
      <div className={styles.calcContainer}>
        <div className={styles.display} aria-live="polite">
          {displayValue}
        </div>

        <div className={styles.grid}>
          <button
            type="button"
            className={styles.btnMemory}
            onClick={handleMemoryClear}
            aria-label="Memory clear"
          >
            MC
          </button>
          <button
            type="button"
            className={styles.btnMemory}
            onClick={handleMemoryRecall}
            aria-label="Memory recall"
          >
            MR
          </button>
          <button
            type="button"
            className={styles.btnMemory}
            onClick={handleMemoryAdd}
            aria-label="Memory add"
          >
            M+
          </button>
          <button
            type="button"
            className={styles.btnMemory}
            onClick={handleMemorySub}
            aria-label="Memory subtract"
          >
            M−
          </button>
          <button
            type="button"
            className={styles.btnClear}
            onClick={handleClearEntry}
            aria-label="Clear entry"
          >
            CE
          </button>

          <button type="button" className={styles.btnNum} onClick={() => handleNumber("7")}>
            7
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("8")}>
            8
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("9")}>
            9
          </button>
          <button
            type="button"
            className={styles.btnOp}
            onClick={() => handleOperator("÷")}
            aria-label="Divide"
          >
            ÷
          </button>
          <button
            type="button"
            className={styles.btnFunc}
            onClick={handleSqrt}
            aria-label="Square root"
          >
            √
          </button>

          <button type="button" className={styles.btnNum} onClick={() => handleNumber("4")}>
            4
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("5")}>
            5
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("6")}>
            6
          </button>
          <button
            type="button"
            className={styles.btnOp}
            onClick={() => handleOperator("×")}
            aria-label="Multiply"
          >
            ×
          </button>
          <button
            type="button"
            className={styles.btnFunc}
            onClick={handlePercent}
            aria-label="Percent"
          >
            %
          </button>

          <button type="button" className={styles.btnNum} onClick={() => handleNumber("1")}>
            1
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("2")}>
            2
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("3")}>
            3
          </button>
          <button
            type="button"
            className={styles.btnOp}
            onClick={() => handleOperator("−")}
            aria-label="Subtract"
          >
            −
          </button>
          <button
            type="button"
            className={styles.btnFunc}
            onClick={handleReciprocal}
            aria-label="Reciprocal"
          >
            1/x
          </button>

          <button type="button" className={styles.btnNum} onClick={() => handleNumber("0")}>
            0
          </button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber(".")}>
            .
          </button>
          <button
            type="button"
            className={styles.btnNum}
            onClick={handleSign}
            aria-label="Toggle sign"
          >
            +/−
          </button>
          <button
            type="button"
            className={styles.btnOp}
            onClick={() => handleOperator("+")}
            aria-label="Add"
          >
            +
          </button>
          <button
            type="button"
            className={styles.btnEquals}
            onClick={handleEquals}
            aria-label="Equals"
          >
            =
          </button>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnFullScreen}
            onClick={toggleFullScreen}
            aria-label={isFullScreen ? "Exit full screen" : "Use full screen"}
          >
            {isFullScreen ? <Minimize2 size={18} aria-hidden /> : <Maximize2 size={18} aria-hidden />}
            <span>{isFullScreen ? "Exit Full Screen" : "Use the Online Calculator Full Screen"}</span>
          </button>
        </div>
      </div>

      {!isFullScreen && (
        <p className={styles.backLink}>
          <Link href="/categories/calculator-tools">← Back to Calculator tools</Link>
        </p>
      )}
    </div>
  );
}
